import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "stockhist__head" }
const _hoisted_2 = { class: "stockhist__meta" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = {
  key: 0,
  class: "stockhist__pick"
}
const _hoisted_6 = ["value"]
const _hoisted_7 = ["value"]
const _hoisted_8 = {
  key: 1,
  class: "stockhist__loading"
}
const _hoisted_9 = {
  class: "stockhist__summary",
  "data-tour": "stock-history-summary"
}
const _hoisted_10 = {
  class: "stockhist__add",
  "data-tour": "stock-history-add"
}
const _hoisted_11 = { class: "stockhist__add-row" }
const _hoisted_12 = ["disabled", "onKeydown"]
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 0,
  class: "stockhist__error"
}
const _hoisted_15 = {
  class: "stockhist__timeline",
  "data-tour": "stock-history-timeline"
}
const _hoisted_16 = {
  key: 0,
  class: "stockhist__list"
}
const _hoisted_17 = { class: "stockhist__row-top" }
const _hoisted_18 = {
  key: 0,
  class: "stockhist__row-main"
}
const _hoisted_19 = { class: "is-sale" }
const _hoisted_20 = {
  key: 1,
  class: "stockhist__row-main"
}
const _hoisted_21 = {
  key: 2,
  class: "stockhist__row-sub"
}
const _hoisted_22 = {
  key: 1,
  class: "stockhist__empty-box"
}
const _hoisted_23 = { class: "stockhist__quick" }
const _hoisted_24 = ["disabled"]
const _hoisted_25 = ["disabled"]
const _hoisted_26 = ["disabled"]
const _hoisted_27 = {
  key: 3,
  class: "stockhist__empty"
}
const _hoisted_28 = {
  key: 4,
  class: "stockhist__empty"
}
const _hoisted_29 = { class: "stockhist__foot" }

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  addAdminProductStock,
  fetchAdminProductStockHistory,
} from '@/api/admin'
import {
  addSellerProductStock,
  fetchSellerProductStockHistory,
  type ProductStockAdjustRow,
  type ProductStockHistory,
  type ProductStockSaleRow,
} from '@/api/mallProduct'

type TimelineSale = ProductStockSaleRow & { kind: 'sale' }
type TimelineAdjust = ProductStockAdjustRow & { kind: 'adjust' }
type TimelineItem = TimelineSale | TimelineAdjust


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductStockHistoryModal',
  props: {
    productCode: {},
    productName: {},
    audience: {},
    candidates: { default: () => [] }
  },
  emits: ["close", "select", "updated"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const titleId = `stockhist-${Math.random().toString(36).slice(2, 7)}`
const panelEl = ref<HTMLElement | null>(null)
const loading = ref(Boolean(props.productCode))
const saving = ref(false)
const detail = ref<ProductStockHistory | null>(null)
const addQty = ref<number | null>(null)
const formError = ref('')

const canAdd = computed(() => {
  const n = Number(addQty.value)
  return Number.isFinite(n) && Math.trunc(n) >= 1
})

const timeline = computed<TimelineItem[]>(() => {
  if (!detail.value) return []
  const sales: TimelineItem[] = (detail.value.sales || []).map((row) => ({ ...row, kind: 'sale' as const }))
  const adjusts: TimelineItem[] = (detail.value.adjustments || []).map((row) => ({
    ...row,
    kind: 'adjust' as const,
  }))
  return [...sales, ...adjusts].sort((a, b) => {
    const ta = new Date(a.at).getTime()
    const tb = new Date(b.at).getTime()
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0
    if (Number.isNaN(ta)) return 1
    if (Number.isNaN(tb)) return -1
    return tb - ta
  })
})

function roleLabel(role: string): string {
  if (role === 'ADMIN') return '관리자'
  if (role === 'SELLER') return '판매업체'
  return role || ''
}

/** 내부 코드(SLR…)는 사람에게 안 보여 준다. */
function actorLabel(item: { role: string; actor: string }): string {
  if (item.role === 'ADMIN') return '관리자'
  if (item.role === 'SELLER') {
    const store = detail.value?.storeName?.trim()
    if (props.audience === 'admin' && store) return store
    return '판매업체'
  }
  return roleLabel(item.role)
}

function badgeLabel(item: TimelineItem): string {
  if (item.kind === 'sale') return '판매'
  if (item.memo.includes('복구')) return '복구'
  if (item.memo.includes('추가')) return '입고'
  return '조정'
}

function badgeClass(item: TimelineItem): string {
  if (item.kind === 'sale') return 'is-sale'
  if (item.memo.includes('복구')) return 'is-restore'
  return 'is-adjust'
}

function formatDateTime(value?: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16)
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function onPick(event: Event) {
  emit('select', (event.target as HTMLSelectElement).value)
}

async function load() {
  if (!props.productCode) {
    loading.value = false
    detail.value = null
    formError.value = ''
    return
  }
  loading.value = true
  formError.value = ''
  try {
    detail.value =
      props.audience === 'admin'
        ? await fetchAdminProductStockHistory(props.productCode)
        : await fetchSellerProductStockHistory(props.productCode)
  } catch (error) {
    detail.value = null
    formError.value = error instanceof Error ? error.message : '이력을 불러오지 못했습니다.'
  } finally {
    loading.value = false
    requestAnimationFrame(() => panelEl.value?.focus())
  }
}

async function onAdd() {
  if (!canAdd.value || saving.value || !detail.value) return
  const qty = Math.trunc(Number(addQty.value))
  formError.value = ''
  saving.value = true
  try {
    if (props.audience === 'admin') {
      const product = await addAdminProductStock(props.productCode, qty)
      emit('updated', { code: product.code, stock: product.stock, status: product.status })
    } else {
      const data = await addSellerProductStock(props.productCode, qty)
      emit('updated', {
        code: props.productCode,
        stock: data.stock,
        status: data.status,
      })
    }
    addQty.value = null
    await load()
  } catch (error) {
    formError.value = error instanceof Error ? error.message : '재고 추가에 실패했습니다.'
  } finally {
    saving.value = false
  }
}

async function quickAdd(qty: number) {
  addQty.value = qty
  await onAdd()
}

watch(
  () => props.productCode,
  () => {
    void load()
  },
)

onMounted(() => {
  document.body.style.overflow = 'hidden'
  void load()
})

onUnmounted(() => {
  document.body.style.overflow = ''
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "stockhist",
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": titleId,
    onKeydown: _cache[7] || (_cache[7] = _withKeys(_withModifiers(($event: any) => (emit('close')), ["prevent"]), ["esc"]))
  }, [
    _createElementVNode("button", {
      type: "button",
      class: "stockhist__catch",
      "aria-label": "닫기",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
    }),
    _createElementVNode("div", {
      class: "stockhist__panel",
      tabindex: "-1",
      ref_key: "panelEl",
      ref: panelEl
    }, [
      _createElementVNode("header", _hoisted_1, [
        _createElementVNode("div", null, [
          _createElementVNode("h2", { id: titleId }, "재고 · 판매 이력"),
          _createElementVNode("p", _hoisted_2, [
            _createElementVNode("strong", null, _toDisplayString(detail.value?.name || __props.productName || '상품을 선택하세요'), 1),
            (detail.value?.code || __props.productCode)
              ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(detail.value?.code || __props.productCode), 1))
              : _createCommentVNode("", true),
            (detail.value?.storeName)
              ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(detail.value.storeName), 1))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "stockhist__close",
          "aria-label": "재고 닫기",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-close",
            size: 20
          })
        ])
      ]),
      (__props.candidates.length)
        ? (_openBlock(), _createElementBlock("label", _hoisted_5, [
            _cache[9] || (_cache[9] = _createElementVNode("span", null, "상품", -1)),
            _createElementVNode("select", {
              class: "sui-input",
              value: __props.productCode,
              onChange: onPick
            }, [
              _cache[8] || (_cache[8] = _createElementVNode("option", { value: "" }, "상품을 선택하세요", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.candidates, (item) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: item.code,
                  value: item.code
                }, _toDisplayString(item.name), 9, _hoisted_7))
              }), 128))
            ], 40, _hoisted_6)
          ]))
        : _createCommentVNode("", true),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(AppIcon, {
              icon: "mdi-loading",
              size: 24
            }),
            _cache[10] || (_cache[10] = _createElementVNode("p", null, "불러오는 중…", -1))
          ]))
        : (detail.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("section", _hoisted_9, [
                _createElementVNode("div", null, [
                  _cache[11] || (_cache[11] = _createElementVNode("span", null, "현재 재고", -1)),
                  _createElementVNode("strong", {
                    class: _normalizeClass({ 'is-low': detail.value.stock <= 0 })
                  }, _toDisplayString(detail.value.stock.toLocaleString('ko-KR')) + "개", 3)
                ]),
                _createElementVNode("div", null, [
                  _cache[12] || (_cache[12] = _createElementVNode("span", null, "누적 판매", -1)),
                  _createElementVNode("strong", null, _toDisplayString(detail.value.soldCount.toLocaleString('ko-KR')) + "개", 1)
                ])
              ]),
              _createElementVNode("section", _hoisted_10, [
                _cache[14] || (_cache[14] = _createElementVNode("h3", null, "재고 추가", -1)),
                _cache[15] || (_cache[15] = _createElementVNode("p", { class: "stockhist__hint" }, "입고 수량을 넣으면 이력에 남고, 현재 재고에 더해집니다.", -1)),
                _createElementVNode("div", _hoisted_11, [
                  _cache[13] || (_cache[13] = _createElementVNode("label", {
                    class: "stockhist__sr",
                    for: "stockhist-add"
                  }, "추가 수량", -1)),
                  _withDirectives(_createElementVNode("input", {
                    id: "stockhist-add",
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((addQty).value = $event)),
                    class: "sui-input",
                    type: "number",
                    min: "1",
                    step: "1",
                    placeholder: "예: 20",
                    disabled: saving.value,
                    onKeydown: _withKeys(_withModifiers(onAdd, ["prevent"]), ["enter"])
                  }, null, 40, _hoisted_12), [
                    [
                      _vModelText,
                      addQty.value,
                      void 0,
                      { number: true }
                    ]
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "sui-btn sui-btn--primary",
                    disabled: saving.value || !canAdd.value,
                    onClick: onAdd
                  }, _toDisplayString(saving.value ? '반영 중…' : '재고 추가'), 9, _hoisted_13)
                ]),
                (formError.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(formError.value), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("section", _hoisted_15, [
                _cache[18] || (_cache[18] = _createElementVNode("h3", null, "이력", -1)),
                (timeline.value.length)
                  ? (_openBlock(), _createElementBlock("ol", _hoisted_16, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timeline.value, (item, index) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: `${item.kind}-${item.at}-${index}`
                        }, [
                          _createElementVNode("div", _hoisted_17, [
                            _createElementVNode("span", {
                              class: _normalizeClass(["stockhist__badge", badgeClass(item)])
                            }, _toDisplayString(badgeLabel(item)), 3),
                            _createElementVNode("time", null, _toDisplayString(formatDateTime(item.at)), 1)
                          ]),
                          (item.kind === 'sale')
                            ? (_openBlock(), _createElementBlock("p", _hoisted_18, [
                                _createElementVNode("strong", _hoisted_19, "−" + _toDisplayString(item.qty.toLocaleString('ko-KR')) + "개", 1),
                                _createElementVNode("span", null, _toDisplayString(item.buyerOrg), 1),
                                _createElementVNode("em", null, _toDisplayString(item.orderCode), 1)
                              ]))
                            : (_openBlock(), _createElementBlock("p", _hoisted_20, [
                                _createElementVNode("strong", {
                                  class: _normalizeClass(item.memo.includes('복구') ? 'is-sale' : 'is-add')
                                }, _toDisplayString(item.memo || '재고 조정'), 3),
                                _createElementVNode("span", null, _toDisplayString(actorLabel(item)), 1)
                              ])),
                          (item.kind === 'sale' && (item.option || item.name))
                            ? (_openBlock(), _createElementBlock("p", _hoisted_21, [
                                _createTextVNode(_toDisplayString(item.name), 1),
                                (item.option)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createTextVNode(" · " + _toDisplayString(item.option), 1)
                                    ], 64))
                                  : _createCommentVNode("", true)
                              ]))
                            : _createCommentVNode("", true)
                        ]))
                      }), 128))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_22, [
                      _cache[16] || (_cache[16] = _createElementVNode("p", null, "아직 판매·입고 이력이 없습니다.", -1)),
                      _cache[17] || (_cache[17] = _createElementVNode("p", { class: "stockhist__empty-guide" }, " 입금 확인된 주문이 생기면 판매 차감이 여기에 쌓이고, 아래에서 입고하면 시점이 남습니다. ", -1)),
                      _createElementVNode("div", _hoisted_23, [
                        _createElementVNode("button", {
                          type: "button",
                          class: "sui-btn sui-btn--sm",
                          disabled: saving.value,
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (quickAdd(10)))
                        }, " +10 입고 ", 8, _hoisted_24),
                        _createElementVNode("button", {
                          type: "button",
                          class: "sui-btn sui-btn--sm",
                          disabled: saving.value,
                          onClick: _cache[4] || (_cache[4] = ($event: any) => (quickAdd(20)))
                        }, " +20 입고 ", 8, _hoisted_25),
                        _createElementVNode("button", {
                          type: "button",
                          class: "sui-btn sui-btn--sm",
                          disabled: saving.value,
                          onClick: _cache[5] || (_cache[5] = ($event: any) => (quickAdd(50)))
                        }, " +50 입고 ", 8, _hoisted_26)
                      ])
                    ]))
              ])
            ], 64))
          : (__props.productCode)
            ? (_openBlock(), _createElementBlock("p", _hoisted_27, "이력을 불러오지 못했습니다."))
            : (_openBlock(), _createElementBlock("p", _hoisted_28, "상품을 고르면 재고와 입고·판매 이력이 열립니다.")),
      _createElementVNode("footer", _hoisted_29, [
        _createElementVNode("button", {
          type: "button",
          class: "sui-btn sui-btn--ghost",
          onClick: _cache[6] || (_cache[6] = ($event: any) => (emit('close')))
        }, "닫기")
      ])
    ], 512)
  ], 32))
}
}

})